import {
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CircleX,
  Info,
  X,
  type LucideIcon,
} from 'lucide-react';
import { isValidElement, type ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';
import { cn } from '../lib/utils';

export type ToastType = 'default' | 'info' | 'success' | 'warning' | 'error';

const TYPE_ICON: Record<ToastType, LucideIcon> = {
  default: CircleHelp,
  info: Info,
  success: CircleCheck,
  warning: CircleAlert,
  error: CircleX,
};

/**
 * Per-type styling. Status types use a solid colored surface with inverse
 * (white) text and icon; the default type uses a light surface with primary
 * text.
 */
const TYPE_STYLES: Record<
  ToastType,
  { surface: string; title: string; description: string; icon: string; action: string }
> = {
  default: {
    surface: 'bg-background-surface border-border-muted',
    title: 'text-text-primary',
    description: 'text-text-secondary',
    icon: 'text-text-primary',
    action: 'border-border-muted text-text-primary',
  },
  info: {
    surface: 'bg-status-blue border-status-blue',
    title: 'text-text-inverse',
    description: 'text-text-inverse',
    icon: 'text-text-inverse',
    action: 'border-text-inverse text-text-inverse',
  },
  success: {
    surface: 'bg-status-green border-status-green',
    title: 'text-text-inverse',
    description: 'text-text-inverse',
    icon: 'text-text-inverse',
    action: 'border-text-inverse text-text-inverse',
  },
  warning: {
    surface: 'bg-status-yellow border-status-yellow',
    title: 'text-text-inverse',
    description: 'text-text-inverse',
    icon: 'text-text-inverse',
    action: 'border-text-inverse text-text-inverse',
  },
  error: {
    surface: 'bg-status-red border-status-red',
    title: 'text-text-inverse',
    description: 'text-text-inverse',
    icon: 'text-text-inverse',
    action: 'border-text-inverse text-text-inverse',
  },
};

export interface ToastAction {
  label: ReactNode;
  onClick: () => void;
}

function isToastAction(action: ToastAction | ReactNode): action is ToastAction {
  return (
    typeof action === 'object' && action !== null && !isValidElement(action) && 'onClick' in action
  );
}

export interface ToastProps {
  type?: ToastType;
  title?: ReactNode;
  description?: ReactNode;
  /**
   * Right-side controls. Pass a `{ label, onClick }` config for the standard
   * Figma "Submit" button, or a custom node for a bespoke control cluster.
   */
  action?: ToastAction | ReactNode;
  icon?: boolean;
  closeButton?: boolean;
  onClose?: () => void;
  className?: string;
}

export function Toast({
  type = 'default',
  title,
  description,
  action,
  icon = true,
  closeButton = true,
  onClose,
  className,
}: ToastProps) {
  const styles = TYPE_STYLES[type];
  const Icon = TYPE_ICON[type];

  return (
    <div
      className={cn(
        'tw flex w-[370px] max-w-[calc(100vw-2rem)] items-center gap-2 rounded-[4px] border border-solid p-3 shadow-[0px_2px_15px_-3px_rgba(0,64,76,0.1),0px_4px_6px_-2px_rgba(0,64,76,0.05)]',
        styles.surface,
        className,
      )}
      data-slot="toast"
      data-type={type}
      role={type === 'error' || type === 'warning' ? 'alert' : 'status'}
    >
      {icon && (
        <span className={cn('flex shrink-0 items-start self-stretch py-0.5', styles.icon)}>
          <Icon className="size-4" aria-hidden />
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        {title && <div className={cn('w-full break-words heading-md', styles.title)}>{title}</div>}
        {description && (
          <div className={cn('w-full break-words body-md', styles.description)}>{description}</div>
        )}
      </div>
      {action &&
        (isToastAction(action) ? (
          <button
            type="button"
            onClick={action.onClick}
            className={cn(
              'inline-flex max-h-12 min-h-8 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-solid bg-transparent px-3 py-1 label-sm',
              styles.action,
            )}
          >
            {action.label}
          </button>
        ) : (
          <div className="flex shrink-0 items-center gap-2">{action}</div>
        ))}
      {closeButton && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={cn(
            'flex shrink-0 items-center justify-center rounded-[4px] p-1 outline-none',
            styles.icon,
          )}
        >
          <X className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

/** Card props plus the type, minus the sonner-managed `onClose`. */
export type ToastContentProps = Omit<ToastProps, 'onClose'>;

/** Extra sonner options (duration, position, id, ...) accepted by `showToast`. */
type ToastLaunchOptions = NonNullable<Parameters<typeof sonnerToast.custom>[1]>;

function launchToast(props: ToastContentProps, options?: ToastLaunchOptions) {
  return sonnerToast.custom(
    (id) => <Toast {...props} onClose={() => sonnerToast.dismiss(id)} />,
    // `unstyled` stops sonner from wrapping the card in its own chrome and
    // width, so the design-system card controls its own appearance.
    { unstyled: true, ...options },
  );
}

type ToastVariantProps = Omit<ToastContentProps, 'type'>;

const variant =
  (type: ToastType) =>
  (props: ToastVariantProps = {}, options?: ToastLaunchOptions) =>
    launchToast({ ...props, type }, options);

/**
 * Show a design-system Toast through sonner. Requires the `<Toaster />` from
 * this package to be mounted. Returns the sonner toast id.
 *
 * @example
 *   showToast.success({ title: 'Saved', description: 'Your changes are live.' });
 *   showToast({ type: 'error', title: 'Failed', action: { label: 'Retry', onClick: retry } });
 */
export const showToast = Object.assign(launchToast, {
  default: variant('default'),
  info: variant('info'),
  success: variant('success'),
  warning: variant('warning'),
  error: variant('error'),
  /** Dismiss a toast by id, or all toasts when no id is given. */
  dismiss: (toastId?: string | number): void => {
    sonnerToast.dismiss(toastId);
  },
});
